import { ProductInterface } from 'common/types/entities/ProductInterface'
import React from 'react'
import {
  OfferLineDataTestProps,
  OfferLineItem,
  OfferLineUiProps,
} from 'src/shared/components/offer-price/offer-line-item/offer-line-item'
import { ProductWrapper } from 'src/shared/components/offer-price/physical-product/ui/product-wrapper'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { CalculatePriceResponse } from 'src/shared/types/pricing-types'
import { checkIsTaxInclusive } from 'src/shared/utils/price-calculator'

export interface ProductProps extends OfferLineDataTestProps {
  product?: ProductInterface | null
  offerPricing?: CalculatePriceResponse
  offerLineUiProps: OfferLineUiProps
  locale: string
}

const COUPON_COLOR = 'rgba(34, 139, 34)'

export const PhysicalProduct = ({
  offerPricing,
  product,
  locale,

  offerLineUiProps,

  dataTestIdName,
  dataTestIdPrice,
  dataTestIdCurrency,
  dataTestIdTax,
}: ProductProps) => {
  const { t } = useTypedTranslation(['common'])
  const isTaxInclusive =
    !!product?.pricePlan && checkIsTaxInclusive(product?.pricePlan)
  const isDiscountApplied =
    !!offerPricing?.discount.amount && offerPricing?.discount.amount > 0
  const isShippingFeeApplied =
    !!offerPricing?.shippingFee.amount && offerPricing?.shippingFee.amount > 0
  const shouldDisplayTax = !isTaxInclusive && !!offerPricing?.tax.amount
  const shouldDisplaySubtotal =
    shouldDisplayTax || isDiscountApplied || isShippingFeeApplied

  // temporary workaround, see: https://systeme-team.slack.com/archives/C07BQERU31B/p1778074786164129
  const calculatedAmount = isTaxInclusive
    ? (offerPricing?.originalAmount ?? 0) * (offerPricing?.quantity ?? 1)
    : offerPricing?.netAmount

  return (
    <ProductWrapper>
      <OfferLineItem
        label={product?.name}
        amount={calculatedAmount}
        currency={offerPricing?.currencyCode}
        tax={isTaxInclusive ? offerPricing?.tax : undefined}
        locale={locale}
        dataTestIdName={`${dataTestIdName}-price`}
        dataTestIdPrice={`${dataTestIdPrice}-price`}
        dataTestIdCurrency={`${dataTestIdCurrency}-price`}
        dataTestIdTax={`${dataTestIdTax}-price`}
        {...offerLineUiProps}
      />
      {isDiscountApplied && (
        <OfferLineItem
          label={t('components.offer_price.coupon')}
          amount={-offerPricing?.discount.amount}
          currency={offerPricing?.currencyCode}
          locale={locale}
          dataTestIdName={`${dataTestIdName}-coupon`}
          dataTestIdPrice={`${dataTestIdPrice}-coupon`}
          dataTestIdCurrency={`${dataTestIdCurrency}-coupon`}
          dataTestIdTax={`${dataTestIdTax}-coupon`}
          {...offerLineUiProps}
          nameColor={COUPON_COLOR}
          mobileNameColor={COUPON_COLOR}
          amountColor={COUPON_COLOR}
          mobileAmountColor={COUPON_COLOR}
        />
      )}
      {shouldDisplayTax && (
        <OfferLineItem
          label={`${t('components.offer_price.vat')} (${offerPricing?.tax.rate}%)`}
          amount={offerPricing?.tax.amount}
          currency={offerPricing?.currencyCode}
          locale={locale}
          dataTestIdName={`${dataTestIdName}-inclusive`}
          dataTestIdPrice={`${dataTestIdPrice}-inclusive`}
          dataTestIdCurrency={`${dataTestIdCurrency}-inclusive`}
          dataTestIdTax={`${dataTestIdTax}-inclusive`}
          {...offerLineUiProps}
        />
      )}

      {isShippingFeeApplied && (
        <>
          <OfferLineItem
            label={t('components.offer_price.shipping')}
            amount={offerPricing?.shippingFee.amount}
            currency={offerPricing?.currencyCode}
            locale={locale}
            dataTestIdName={`${dataTestIdName}-shipping-price`}
            dataTestIdPrice={`${dataTestIdPrice}-shipping-price`}
            dataTestIdCurrency={`${dataTestIdCurrency}-shipping-price`}
            dataTestIdTax={`${dataTestIdTax}-shipping-price`}
            {...offerLineUiProps}
          />
          {!!offerPricing?.shippingFee.tax.amount && (
            <OfferLineItem
              label={`${t('components.offer_price.vat')} (${offerPricing?.shippingFee.tax.rate}%)`}
              amount={offerPricing?.shippingFee.tax.amount}
              currency={offerPricing?.currencyCode}
              locale={locale}
              dataTestIdName={`${dataTestIdName}-shipping-tax`}
              dataTestIdPrice={`${dataTestIdPrice}-shipping-tax`}
              dataTestIdCurrency={`${dataTestIdCurrency}-shipping-tax`}
              dataTestIdTax={`${dataTestIdTax}-shipping-tax`}
              {...offerLineUiProps}
            />
          )}
        </>
      )}
      {shouldDisplaySubtotal && (
        <OfferLineItem
          label={t('components.offer_price.subtotal')}
          amount={offerPricing?.grossAmount}
          currency={offerPricing?.currencyCode}
          locale={locale}
          dataTestIdName={`${dataTestIdName}-subtotal`}
          dataTestIdPrice={`${dataTestIdPrice}-subtotal`}
          dataTestIdCurrency={`${dataTestIdCurrency}-subtotal`}
          dataTestIdTax={`${dataTestIdTax}-subtotal`}
          {...offerLineUiProps}
        />
      )}
    </ProductWrapper>
  )
}
